import React, { useEffect, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { Moon, Clock, Check, X, ArrowRight, Star } from 'lucide-react';
// Meta Pixel initialization
const META_PIXEL_ID = '903691875644616';
function initMetaPixel() {
  if (typeof window !== 'undefined' && !(window as any).fbq) {
    const f = window as any;
    const n = f.fbq = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [] as any[];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    (window as any).fbq('init', META_PIXEL_ID);
    (window as any).fbq('track', 'PageView');
  }
}
function trackPurchaseClick() {
  if (typeof window !== 'undefined' && (window as any).fbq) {
    ;
    (window as any).fbq('track', 'InitiateCheckout', {
      content_name: 'Kit Dormí de Nuevo 60+',
      content_category: 'Ebook',
      value: 9585,
      currency: 'ARS'
    });
  }
}
// Animation variants for fade-in effect
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
// Reusable Section Component
const Section = ({
  children,
  className = ''
}: {
  children: ReactNode;
  className?: string;
}) => <motion.section initial="hidden" whileInView="visible" viewport={{
  once: true,
  margin: '-100px'
}} variants={fadeInUp} className={`py-16 md:py-24 px-6 md:px-8 max-w-3xl mx-auto ${className}`} data-id="element-1">
    {children}
  </motion.section>;
// Reusable Divider
const Divider = () => <div className="w-24 h-px bg-[#c9a96e] mx-auto my-12 opacity-50" data-id="element-2" />;
// Reusable Button
const CTAButton = ({
  children,
  className = '',
  onClick,
  href
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  href?: string;
}) => {
  const classes = `w-full md:w-auto bg-[#c9a96e] hover:bg-[#d4a853] text-[#0f1629] font-bold text-xl md:text-2xl py-4 px-8 rounded-lg shadow-lg transform transition-transform hover:scale-105 active:scale-95 flex items-center justify-center gap-3 ${className}`;
  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className={classes} onClick={e => {
      if (onClick) onClick();
    }} data-id="element-3">
        {children}
        <ArrowRight className="w-6 h-6" data-id="element-4" />
      </a>;
  }
  return <button onClick={onClick} className={classes} data-id="element-5">
      {children}
      <ArrowRight className="w-6 h-6" data-id="element-6" />
    </button>;
};
export function SalesPage() {
  useEffect(() => {
    initMetaPixel();
  }, []);
  return <div className="min-h-screen bg-[#0f1629] text-[#f5f0e8] font-sans selection:bg-[#c9a96e] selection:text-[#0f1629]" data-id="element-7">
      <style data-id="element-8">{`
        @import url('https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400&display=swap');
        .font-serif { font-family: 'Merriweather', Georgia, serif; }
      `}</style>

      {/* Hero Section */}
      <header className="pt-12 pb-16 md:pt-20 md:pb-24 px-6 relative overflow-hidden" data-id="element-9">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#1a2341] to-[#0f1629] z-0" data-id="element-10" />

        <div className="max-w-3xl mx-auto relative z-10" data-id="element-11">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1
        }} className="space-y-6 text-lg md:text-xl leading-relaxed text-[#e8e0d0] mb-12 font-serif italic" data-id="element-12">
            <p data-id="element-13">Son las 3 y pico.</p>
            <p data-id="element-14">Tu cuerpo está incómodo. A veces calor, a veces sudor.</p>
            <p data-id="element-15">Te acomodás. Te destapás. Te tapás.</p>
            <p data-id="element-16">Y aparece la frase que te parte la noche en dos:</p>
            <p className="font-bold text-[#c9a96e]" data-id="element-17">
              «Listo. Mañana no rindo.»
            </p>
            <p data-id="element-18">
              A partir de ahí, ya no es sueño. Es negociación. Es bronca. Es
              miedo.
            </p>
            <p data-id="element-19">Es estar cansada… y a la vez demasiado despierta.</p>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 0.8
        }} className="text-3xl md:text-5xl font-serif font-bold text-[#c9a96e] leading-tight mb-6" data-id="element-20">
            Si te despertás a las 3 AM, esto no es «nervios».
          </motion.h1>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7,
          duration: 0.8
        }} className="text-xl md:text-2xl text-[#f5f0e8] mb-12 font-light" data-id="element-21">
            No te falta fuerza de voluntad. Te falta un plan para el momento
            exacto.
          </motion.h2>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.9,
          duration: 0.8
        }} className="mb-10 rounded-xl overflow-hidden shadow-2xl border border-[#c9a96e]/20" data-id="element-22">
            <img src="https://cdn.magicpatterns.com/uploads/77XnfA1u1CChhCLXf12RK1/Ebook.png" alt="Dormí de Nuevo (60+) Ebook Cover" className="w-full h-auto object-cover" data-id="element-23" />
          </motion.div>

          <div className="text-center space-y-8" data-id="element-24">
            <p className="text-xl md:text-2xl font-serif text-[#c9a96e]" data-id="element-25">
              El Kit «Mapa 3AM»: 14 noches para dejar de pelearte con la
              madrugada.
            </p>
            <CTAButton onClick={() => document.getElementById('comprar')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-26">
              Quiero el Kit Mapa 3AM
            </CTAButton>
          </div>
        </div>
      </header>

      <Divider data-id="element-27" />

      {/* Pattern Section */}
      <Section data-id="element-28">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#c9a96e] mb-8" data-id="element-29">
          Esto no es «drama». Es un patrón.
        </h2>
        <div className="space-y-6 text-lg md:text-xl text-[#e8e0d0] leading-relaxed" data-id="element-30">
          <p data-id="element-31">
            Si te pasa seguido, ya sabés que no es una noche aislada. Es
            repetitivo. Y lo repetitivo cansa más que lo intenso.
          </p>
          <p data-id="element-32">Lo peor no es despertarte. Lo peor es lo que viene después:</p>
          <ul className="pl-4 space-y-3 border-l-2 border-[#c9a96e]/30 my-6" data-id="element-33">
            <li className="pl-4" data-id="element-34">— mirar el techo</li>
            <li className="pl-4" data-id="element-35">— sentir que el cuerpo no coopera</li>
            <li className="pl-4" data-id="element-36">
              — y escuchar tu propia mente diciendo cualquier cosa con autoridad
            </li>
          </ul>
          <p className="font-serif italic text-xl text-[#f5f0e8]" data-id="element-37">
            A esa hora, tu mente no está «pensando». Está activando alarma.
          </p>
        </div>
      </Section>

      <Divider data-id="element-38" />

      {/* Why Old Solutions Fail */}
      <Section data-id="element-39">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#c9a96e] mb-8" data-id="element-40">
          ¿Por qué casi todo lo que probaste no te sirve?
        </h2>
        <div className="space-y-6 text-lg md:text-xl text-[#e8e0d0] leading-relaxed" data-id="element-41">
          <p data-id="element-42">
            Porque la mayoría de los consejos están hechos para la parte «linda»
            del problema.
          </p>
          <p data-id="element-43">La parte linda es:</p>
          <ul className="flex flex-wrap gap-3 mb-6" data-id="element-44">
            {['acostarte temprano', 'relajarte', '«hacer higiene del sueño»'].map((item, i) => <span key={i} className="bg-[#1a2341] border border-[#c9a96e]/30 px-4 py-2 rounded-full text-[#c9a96e]" data-id="element-45">
                {item}
              </span>)}
          </ul>
          <div className="bg-[#1a2341] p-6 rounded-lg border-l-4 border-[#c9a96e]" data-id="element-46">
            <p className="mb-4" data-id="element-47">
              Pero tu problema real está en la parte fea:{' '}
              <span className="text-[#f5f0e8] font-bold" data-id="element-48">
                cuando ya estás despierta.
              </span>
            </p>
            <p data-id="element-49">
              Ahí nadie te acompaña. Ahí te dejan con frases sueltas. Y con
              culpa.
            </p>
          </div>
        </div>
      </Section>

      <Divider data-id="element-50" />

      {/* Mechanism Intro */}
      <Section data-id="element-51">
        <div className="text-center max-w-2xl mx-auto" data-id="element-52">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#f5f0e8] mb-8" data-id="element-53">
            El Kit «Dormí de Nuevo (60+)» no te pide que seas perfecta
          </h2>
          <p className="text-xl md:text-2xl text-[#c9a96e] mb-8 font-serif italic" data-id="element-54">
            Te pide que seas constante 14 noches. Nada más.
          </p>
          <p className="text-lg md:text-xl text-[#e8e0d0] leading-relaxed" data-id="element-55">
            Porque en 14 noches se ven dos cosas que hoy están mezcladas:
            <br data-id="element-56" />
            <br data-id="element-57" />
            1) qué te dispara
            <br data-id="element-58" />
            2) qué te saca del pozo cuando ya caíste
          </p>
        </div>
      </Section>

      {/* Method Section */}
      <Section className="bg-[#151c33] rounded-2xl my-12 shadow-xl border border-[#c9a96e]/10" data-id="element-59">
        <div className="text-center mb-12" data-id="element-60">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#c9a96e] mb-4" data-id="element-61">
            Presento: el método «Mapa 3AM»
          </h2>
          <p className="text-xl text-[#f5f0e8] opacity-80" data-id="element-62">
            No es magia. Es estructura.
          </p>
        </div>

        <div className="grid gap-8" data-id="element-63">
          {/* Part 1 */}
          <div className="bg-[#0f1629] p-8 rounded-xl border border-[#c9a96e]/20 relative overflow-hidden group hover:border-[#c9a96e]/50 transition-colors" data-id="element-64">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-65">
              <Clock size={64} data-id="element-66" />
            </div>
            <h3 className="text-2xl font-serif font-bold text-[#f5f0e8] mb-4 flex items-center gap-3" data-id="element-67">
              <span className="bg-[#c9a96e] text-[#0f1629] w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" data-id="element-68">
                1
              </span>
              Tracker mínimo (60 segundos)
            </h3>
            <p className="text-[#e8e0d0] text-lg leading-relaxed mb-4" data-id="element-69">
              No para obsesionarte. Para que tu cerebro deje de decir «me pasa
              porque sí».
            </p>
            <div className="bg-[#1a2341] p-4 rounded-lg text-[#c9a96e] italic text-base space-y-2" data-id="element-70">
              <p data-id="element-71">«Las noches peores son cuando…»</p>
              <p data-id="element-72">«El primer despertar aparece después de…»</p>
              <p data-id="element-73">«Con X cambio, tardo menos en volver.»</p>
            </div>
            <p className="mt-4 text-[#f5f0e8] font-bold" data-id="element-74">Eso es oro.</p>
          </div>

          {/* Part 2 */}
          <div className="bg-[#0f1629] p-8 rounded-xl border border-[#c9a96e]/20 relative overflow-hidden group hover:border-[#c9a96e]/50 transition-colors" data-id="element-75">
            <h3 className="text-2xl font-serif font-bold text-[#f5f0e8] mb-4 flex items-center gap-3" data-id="element-76">
              <span className="bg-[#c9a96e] text-[#0f1629] w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" data-id="element-77">
                2
              </span>
              Gatillos (los sospechosos habituales)
            </h3>
            <p className="text-[#e8e0d0] text-lg leading-relaxed mb-4" data-id="element-78">
              No vamos a cambiar tu vida completa. Vamos a tocar 1–2 palancas
              que tengan impacto.
            </p>
            <p className="text-[#c9a96e] text-lg mb-4" data-id="element-79">
              Temperatura. Azúcar tarde. Alcohol. Pantallas. Siestas. Estrés
              acumulado.
            </p>
            <p className="text-[#f5f0e8] font-bold" data-id="element-80">
              Pequeño. Medible. Sostenible.
            </p>
          </div>

          {/* Part 3 */}
          <div className="bg-[#0f1629] p-8 rounded-xl border border-[#c9a96e]/20 relative overflow-hidden group hover:border-[#c9a96e]/50 transition-colors" data-id="element-81">
            <h3 className="text-2xl font-serif font-bold text-[#f5f0e8] mb-4 flex items-center gap-3" data-id="element-82">
              <span className="bg-[#c9a96e] text-[#0f1629] w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" data-id="element-83">
                3
              </span>
              Protocolo 7 minutos
            </h3>
            <p className="text-[#e8e0d0] text-lg leading-relaxed mb-4" data-id="element-84">
              Cuando te despertás, tu trabajo no es «dormirte ya». Tu trabajo es
              no entrar en espiral. El Protocolo 7 minutos te da un orden:
            </p>
            <ol className="list-decimal pl-5 space-y-2 text-[#c9a96e] mb-4" data-id="element-85">
              <li data-id="element-86">señal de seguridad</li>
              <li data-id="element-87">cuerpo primero</li>
              <li data-id="element-88">apagar chispa mental</li>
              <li data-id="element-89">puente al segundo sueño</li>
            </ol>
            <p className="text-[#f5f0e8] font-bold" data-id="element-90">
              Sin improvisar. Sin negociar.
            </p>
          </div>

          {/* Part 4 */}
          <div className="bg-[#0f1629] p-8 rounded-xl border border-[#c9a96e]/20 relative overflow-hidden group hover:border-[#c9a96e]/50 transition-colors" data-id="element-91">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-92">
              <Moon size={64} data-id="element-93" />
            </div>
            <h3 className="text-2xl font-serif font-bold text-[#f5f0e8] mb-4 flex items-center gap-3" data-id="element-94">
              <span className="bg-[#c9a96e] text-[#0f1629] w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" data-id="element-95">
                4
              </span>
              El «Segundo Sueño»
            </h3>
            <p className="text-[#e8e0d0] text-lg leading-relaxed mb-4" data-id="element-96">
              La mayoría pierde horas porque se queda en la cama luchando. Acá
              aprendés el movimiento simple: si la cabeza se prende, hacés un
              cambio de escena breve y volvés cuando aparece sueño.
            </p>
            <p className="text-[#f5f0e8] font-bold" data-id="element-97">
              Es contraintuitivo. Y por eso funciona.
            </p>
          </div>
        </div>
      </Section>

      {/* What You Get */}
      <Section data-id="element-98">
        <div className="bg-[#c9a96e]/10 border border-[#c9a96e] rounded-2xl p-8 md:p-12" data-id="element-99">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#c9a96e] mb-8 text-center" data-id="element-100">
            Lo que recibís (instantáneo)
          </h2>
          <ul className="space-y-4 mb-8" data-id="element-101">
            {['PDF completo «Dormí de Nuevo (60+)»', 'Tracker «Mapa 3AM» (14 días)', 'Checklist «Dormitorio anti-desvelo»', 'Tarjeta recortable «3AM» (para mesa de luz)'].map((item, i) => <li key={i} className="flex items-start gap-4 text-lg md:text-xl text-[#f5f0e8]" data-id="element-102">
                <Check className="w-6 h-6 text-[#c9a96e] flex-shrink-0 mt-1" data-id="element-103" />
                <span data-id="element-104">{item}</span>
              </li>)}
          </ul>
          <p className="text-center text-[#e8e0d0] opacity-80 text-sm md:text-base" data-id="element-105">
            (Descarga inmediata + imprimibles)
          </p>
        </div>
      </Section>

      <Divider data-id="element-106" />

      {/* For Whom */}
      <Section data-id="element-107">
        <div className="grid md:grid-cols-2 gap-8" data-id="element-108">
          {/* For You */}
          <div className="bg-[#1a2341] p-8 rounded-xl border-t-4 border-[#c9a96e]" data-id="element-109">
            <h3 className="text-2xl font-serif font-bold text-[#f5f0e8] mb-6 flex items-center gap-2" data-id="element-110">
              <Check className="w-6 h-6 text-[#c9a96e]" data-id="element-111" />
              Para vos si:
            </h3>
            <ul className="space-y-4" data-id="element-112">
              {['te despertás varias noches por semana', 'sentís calor/sudor o ansiedad nocturna', 'estás cansada de «probar cosas» sin entender nada', 'querés una herramienta privada, clara, sin vergüenza'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[#e8e0d0]" data-id="element-113">
                  <span className="text-[#c9a96e] mt-1.5" data-id="element-114">•</span>
                  <span data-id="element-115">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* Not For You */}
          <div className="bg-[#1a2341] p-8 rounded-xl border-t-4 border-gray-600 opacity-80" data-id="element-116">
            <h3 className="text-2xl font-serif font-bold text-gray-300 mb-6 flex items-center gap-2" data-id="element-117">
              <X className="w-6 h-6 text-gray-400" data-id="element-118" />
              No es para vos si:
            </h3>
            <ul className="space-y-4" data-id="element-119">
              {['querés una solución instantánea sin hacer 14 noches', 'buscás promesas médicas o «curas»', 'no querés registrar nada, ni 60 segundos'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-300" data-id="element-120">
                  <span className="text-gray-500 mt-1.5" data-id="element-121">•</span>
                  <span data-id="element-122">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </Section>

      <Divider data-id="element-123" />

      {/* Transformation */}
      <Section data-id="element-124">
        <div className="text-center max-w-2xl mx-auto" data-id="element-125">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#c9a96e] mb-8" data-id="element-126">
            Lo que cambia de verdad
          </h2>
          <div className="space-y-6 text-lg md:text-xl text-[#e8e0d0] leading-relaxed" data-id="element-127">
            <p data-id="element-128">
              No prometo que nunca más te vas a despertar. Eso sería mentirte.
            </p>
            <p data-id="element-129">
              Lo que sí cambia es esto:{' '}
              <span className="text-[#f5f0e8] font-bold" data-id="element-130">
                dejás de sentirte rehén de la madrugada.
              </span>
            </p>
            <p data-id="element-131">Porque cuando te pase, vas a tener:</p>
            <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-8 my-6 text-[#c9a96e] font-serif italic text-xl" data-id="element-132">
              <span data-id="element-133">— un plan</span>
              <span data-id="element-134">— un orden</span>
              <span data-id="element-135">— y evidencia propia</span>
            </div>
            <p className="text-2xl text-[#f5f0e8]" data-id="element-136">Y eso… baja el miedo.</p>
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <footer id="comprar" className="py-20 px-6 bg-[#151c33] border-t border-[#c9a96e]/20" data-id="element-137">
        <div className="max-w-3xl mx-auto text-center space-y-10" data-id="element-138">
          <h2 className="text-2xl md:text-4xl font-serif text-[#f5f0e8] leading-tight" data-id="element-139">
            Si querés dejar de pelearte con la madrugada, y empezar a tratar tu
            sueño como un patrón que se entiende y se entrena:
          </h2>

          <div className="flex flex-col items-center gap-4" data-id="element-140">
            <CTAButton href="https://www.infoproductosx10.com/cart/47048806662322:1" className="w-full md:w-auto px-12 py-6 text-2xl" onClick={trackPurchaseClick} data-id="element-141">
              Comprá «Dormí de Nuevo (60+)»
            </CTAButton>
            <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-142">
              <span className="text-[#e8e0d0]/50 line-through text-xl md:text-2xl" data-id="element-143">
                $19.170
              </span>
              <span className="text-[#c9a96e] font-bold text-3xl md:text-4xl font-serif" data-id="element-144">
                $9.585
              </span>
              <span className="bg-[#c9a96e] text-[#0f1629] font-bold text-sm md:text-base px-3 py-1 rounded-full" data-id="element-145">
                50% OFF
              </span>
            </div>
            <p className="text-[#e8e0d0] opacity-70" data-id="element-146">
              (Descarga inmediata + imprimibles)
            </p>
          </div>

          <div className="pt-12 border-t border-[#c9a96e]/10 mt-10" data-id="element-147">
            <p className="text-xs text-[#e8e0d0]/40 leading-relaxed max-w-2xl mx-auto text-center space-y-3" data-id="element-148">
              <span className="block mb-3 text-[#c9a96e]/50 font-bold text-sm uppercase tracking-wider" data-id="element-149">
                Términos y Condiciones
              </span>
              <span className="block" data-id="element-150">
                Este producto es un material educativo digital de descarga
                inmediata. No constituye asesoramiento médico, psicológico ni
                profesional de ningún tipo. Los resultados pueden variar según
                cada persona. Si tenés un problema de salud, consultá con un
                profesional.
              </span>
              <span className="block mt-2" data-id="element-151">
                Al realizar la compra, aceptás que se trata de un producto
                digital no reembolsable una vez descargado. El contenido es para
                uso personal y no puede ser redistribuido, copiado ni revendido
                sin autorización expresa.
              </span>
              <span className="block mt-2" data-id="element-152">
                Todos los derechos de propiedad intelectual sobre el Kit «Dormí
                de Nuevo (60+)» y el método «Mapa 3AM» pertenecen a sus autores.
                Queda prohibida su reproducción total o parcial.
              </span>
            </p>
            <div className="mt-6 opacity-40 text-center" data-id="element-153">
              <Star className="w-5 h-5 text-[#c9a96e] mx-auto mb-3" data-id="element-154" />
              <p className="text-xs text-[#e8e0d0]" data-id="element-155">
                © 2024 Kit Mapa 3AM. Todos los derechos reservados.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}